/**
 * POST call that dumps the latest medical PA form JSON onto the submission row.
 * @module dumpLatestMedicalPaFormData
 */

import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "./bearerToken";

/**
 * Request payload for {@link dumpLatestMedicalPaFormData}.
 *
 * Mirrors the backend `DumpFormDataRequest` model.
 */
export interface DumpLatestMedicalPaFormDataRequest {
  /** Medical PA order id whose submission row is written. */
  order_id: string;
  /** Tenant org id; sets the RLS context for the write. */
  org_id: string;
  /** Patient id; required only when creating a new submission row. */
  patient_id?: string | null;
  /** Arbitrary form JSON stored verbatim in `latest_form_data`. */
  form_data: Record<string, unknown>;
}

/**
 * Persists the latest form JSON onto the medical PA submission row.
 *
 * @param payload - Order/org identifiers plus the form JSON to store.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The parsed JSON response from the endpoint.
 * @throws {Error} When the HTTP response is not OK (includes status in the message).
 * @throws Re-throws any network or parsing error after logging.
 */
export const dumpLatestMedicalPaFormData = async (
  payload: DumpLatestMedicalPaFormDataRequest,
  signal?: AbortSignal,
): Promise<unknown> => {
  try {
    const bearerToken = await getBearerToken();
    const response = await fetch(
      API_ENDPOINTS.DUMP_LATEST_MEDICAL_PA_FORM_DATA,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify(payload),
        ...(signal && { signal }),
      },
    );
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        `Dump medical PA form-data request failed (${response.status}): ${JSON.stringify(result)}`,
      );
    }
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
